<template>
  <div>
    <div class="page-head">
      <div>
        <h2>其他收入</h2>
        <p>登记非成交收入；确认后进入指定资金账户</p>
      </div>
      <el-button
        type="primary"
        @click="openCreate"
      >
        登记收入
      </el-button>
    </div>
    <el-card shadow="never">
      <el-table
        v-loading="loading"
        :data="rows"
        stripe
      >
        <el-table-column
          prop="income_date"
          label="收入日期"
          width="170"
        />
        <el-table-column
          prop="branch_name"
          label="归属分部"
          width="130"
        >
          <template #default="{ row }">
            {{ row.branch_name || '公司级' }}
          </template>
        </el-table-column>
        <el-table-column
          prop="category"
          label="分类"
          width="120"
        />
        <el-table-column
          prop="purpose"
          label="用途"
          min-width="180"
        />
        <el-table-column
          label="金额"
          width="130"
        >
          <template #default="{ row }">¥ {{ money(row.amount) }}</template>
        </el-table-column>
        <el-table-column
          prop="account_name"
          label="资金账户"
          width="130"
        />
        <el-table-column
          label="状态"
          width="100"
        >
          <template #default="{ row }">
            <StatusTag :status="row.status" />
          </template>
        </el-table-column>
        <el-table-column
          label="操作"
          width="210"
        >
          <template #default="{ row }">
            <el-button
              v-if="row.status === 'DRAFT'"
              link
              type="success"
              @click="confirmRow(row)"
            >
              确认
            </el-button>
            <el-button
              v-if="row.status === 'CONFIRMED'"
              link
              type="danger"
              @click="voidRow(row)"
            >
              冲销
            </el-button>
            <VoucherButton
              biz-type="OTHER_INCOME"
              :biz-id="row.id"
            />
          </template>
        </el-table-column>
      </el-table>
      <el-empty
        v-if="!loading && !rows.length"
        description="暂无其他收入"
      />
    </el-card>
    <el-dialog
      v-model="dialog"
      title="登记其他收入"
      width="580px"
    >
      <el-form
        :model="form"
        label-width="110px"
      >
        <el-form-item
          label="收入日期"
          required
        >
          <el-date-picker
            v-model="form.incomeDate"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="归属分部">
          <el-select
            v-model="form.organizationUnitId"
            clearable
            style="width: 100%"
            placeholder="不选则为公司级"
          >
            <el-option
              v-for="item in branches"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item
          label="收入金额"
          required
        >
          <el-input-number
            v-model="form.amount"
            :min="0.01"
            :precision="4"
            :controls="false"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="分类">
          <el-input v-model="form.category" />
        </el-form-item>
        <el-form-item
          label="用途"
          required
        >
          <el-input
            v-model="form.purpose"
            type="textarea"
          />
        </el-form-item>
        <el-form-item
          label="入账账户"
          required
        >
          <el-select
            v-model="form.accountId"
            style="width: 100%"
          >
            <el-option
              v-for="item in accounts"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="支付方式">
          <el-select
            v-model="form.paymentMethod"
            style="width: 100%"
          >
            <el-option
              v-for="item in methods"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog = false">取消</el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="save"
        >
          保存草稿
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ledgerApi, organizationApi } from '../api'
import StatusTag from '../components/StatusTag.vue'
import VoucherButton from '../components/VoucherButton.vue'

const rows = ref([])
const branches = ref([])
const accounts = ref([])
const methods = ref([])
const loading = ref(false)
const saving = ref(false)
const dialog = ref(false)
const form = reactive({
  incomeDate: '',
  organizationUnitId: null,
  amount: 0,
  category: '',
  purpose: '',
  accountId: null,
  paymentMethod: 'BANK',
})
const money = (value) =>
  Number(value || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
const load = async () => {
  loading.value = true
  try {
    rows.value = await ledgerApi.otherIncome()
  } catch (error) {
    ElMessage.error(error?.message || '收入加载失败')
  } finally {
    loading.value = false
  }
}
const openCreate = async () => {
  const data = await Promise.all([
    organizationApi.branches(),
    ledgerApi.accounts(),
    organizationApi.paymentMethods(),
  ])
  branches.value = data[0].filter((item) => item.enabled)
  accounts.value = data[1].filter((item) => item.enabled)
  methods.value = data[2].filter((item) => item.enabled)
  Object.assign(form, {
    incomeDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
    organizationUnitId: null,
    amount: 0,
    category: '',
    purpose: '',
    accountId: accounts.value[0]?.id || null,
    paymentMethod: methods.value[0]?.code || 'BANK',
  })
  dialog.value = true
}
const save = async () => {
  if (!form.incomeDate || !form.amount || !form.purpose || !form.accountId)
    return ElMessage.warning('请完整填写日期、金额、用途和账户')
  saving.value = true
  try {
    await ledgerApi.createIncome({ ...form })
    ElMessage.success('收入草稿已保存')
    dialog.value = false
    await load()
  } catch (error) {
    ElMessage.error(error?.message || '保存失败')
  } finally {
    saving.value = false
  }
}
const confirmRow = async (row) => {
  try {
    await ElMessageBox.confirm('确认收入并写入资金账户？', '确认')
    await ledgerApi.confirmIncome(row.id)
    await load()
  } catch (error) {
    if (error !== 'cancel') ElMessage.error(error?.message || '确认失败')
  }
}
const voidRow = async (row) => {
  try {
    const { value } = await ElMessageBox.prompt('请输入冲销原因', '冲销收入', {
      inputValidator: (text) => !!text || '原因不能为空',
    })
    await ledgerApi.voidIncome(row.id, value)
    await load()
  } catch (error) {
    if (error !== 'cancel') ElMessage.error(error?.message || '冲销失败')
  }
}
onMounted(load)
</script>
<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-head h2 {
  font-size: 24px;
}
.page-head p {
  color: #8a97a8;
  margin-top: 6px;
}
.el-card {
  border: 0;
}
</style>
